import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Flame, CheckCircle, XCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, CloudRain, RotateCcw, Trophy, HelpCircle, Loader2 } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import { useTelemetry } from '../../../core/hooks/useTelemetry';

// Data Banks
import { mediumMattersBank } from '../../../data/MediumMattersBank';
import { gatekeepingBank } from '../../../data/GatekeepingBank';
import { techStackBank } from '../../../data/TechStackBank'; 

const SwipeGame = ({ gameData, onComplete }) => {
  const { config, id, preGameBriefing, failureLogic } = gameData;
  
  // UX Calibration: Stable initialization with Fisher-Yates array shuffle.
  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
      let sourceDeck = [];
      let sliceCount = 10;
      
      if (id === 'game_channel_surfer') {
          sourceDeck = mediumMattersBank;
          sliceCount = 10;
      } else if (id === 'game_gatekeeper') {
          sourceDeck = gatekeepingBank;
          sliceCount = 5;
      } else if (id === 'tech-stack' || id === 'game_tech_stack') {
          sourceDeck = techStackBank;
          sliceCount = 10;
      } else if (id === 'game_culture_vibe' || id === 'game_dress_code' || id === 'game_expense_roulette' || id === 'game_office_etiquette') {
          // --- CAP FOR CULTURE VIBE, VISUAL RULES, EXPENSE ROULETTE & OFFICE ETIQUETTE ---
          sourceDeck = gameData?.deck || [];
          sliceCount = 10; 
      } else {
          sourceDeck = gameData?.deck || [];
          sliceCount = sourceDeck.length;
      }

      // Fisher-Yates shuffle algorithm for strict randomness
      let array = [...sourceDeck];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }

      // Isolate exact number of unpredictable scenarios
      deckRef.current = array.slice(0, sliceCount);
  }

  const activeDeck = deckRef.current;

  const { playSuccess, playError, playExtinguish, playFire, playWin } = useGameSounds();
  const { transmitTelemetry } = useTelemetry();

  // --- STATE ---
  const [stage, setStage] = useState('briefing'); 
  
  const [index, setIndex] = useState(0);
  const [streak, setStreak] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [isExtinguished, setIsExtinguished] = useState(false);
  const [layout, setLayout] = useState({ left: null, right: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ZERO-TRUST ARRAY TRACKING & TELEMETRY ATTEMPTS
  const [userAnswers, setUserAnswers] = useState([]);
  const [sessionAttempts, setSessionAttempts] = useState([]);
  
  // High-Velocity Decision Latency (TTFI per card)
  const cardMountTimeRef = useRef(null);
  
  const [activeIntervention, setActiveIntervention] = useState(null);

  const card = activeDeck[index];
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const opacity = useTransform(x, [-200, -150, 0, 150, 200], [0, 1, 1, 1, 0]);
  
  // --- 1. HANDLE BRIEFING ON MOUNT ---
  useEffect(() => {
    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('playing');
        cardMountTimeRef.current = Date.now();
    }
  }, [preGameBriefing]);

  useEffect(() => {
    if (!card) return;
    
    // Reset individual card TTFI clock when a new card is mounted
    cardMountTimeRef.current = Date.now();

    if (config.mode === 'horizontal') {
      const itemA = { id: 'A', ...config.labels.A };
      const itemB = { id: 'B', ...config.labels.B };

      if (config.shuffleLabels && Math.random() > 0.5) {
        setLayout({ left: itemB, right: itemA });
      } else {
        setLayout({ left: itemA, right: itemB });
      }
    }
  }, [index, config, card]);

  const handleDragEnd = (event, info) => {
    const threshold = 100;
    const { offset } = info;

    if (config.mode === 'horizontal') {
      if (offset.x > threshold) handleSwipe('right');
      else if (offset.x < -threshold) handleSwipe('left');
    } else if (config.mode === 'vertical') {
      if (offset.y < -threshold) handleSwipe('up');
      else if (offset.y > threshold) handleSwipe('down');
    }
  };

  const handleSwipe = (dir) => {
    const cardInteractionLatency = Date.now() - cardMountTimeRef.current;
    
    let isCorrect = false;
    let chosenAnswer;

    if (config.mode === 'horizontal') {
      const chosen = dir === 'left' ? layout.left : layout.right;
      
      // Support for varying JSON schemas across different scenario banks
      if (id === 'game_channel_surfer') {
          isCorrect = chosen.text === card.correctAnswer;
          chosenAnswer = chosen.text;
      } else if (id === 'tech-stack' || id === 'game_tech_stack') {
          // MODULE 3 INJECTION: Verify against the 'options' array
          const selectedOption = card.options.find(o => o.route === chosen.text);
          isCorrect = selectedOption ? selectedOption.isCorrect : false;
          chosenAnswer = chosen.text;
      } else {
          isCorrect = chosen.id === card.correctId;
          chosenAnswer = chosen.id;
      }
    } else if (config.mode === 'vertical') {
      if (id === 'game_gatekeeper') {
          chosenAnswer = dir === 'up' ? "Do It Now" : "Schedule It";
          isCorrect = chosenAnswer === card.correctAnswer;
      } else {
          isCorrect = dir === card.correctDirection;
          chosenAnswer = dir;
      }
    }

    // Store the answer choice and High-Velocity Decision Latency for the Zero-Trust Payload
    setUserAnswers(prev => [...prev, { answer: chosenAnswer, latencyMs: cardInteractionLatency }]);

    // Pull feedbackRationale string directly from the object if present
    let responseText = card.rationale || card.feedbackRationale || card.feedback;
    
    // Module 3 Feedback Parsing
    if (id === 'tech-stack' || id === 'game_tech_stack') {
        const selectedOption = card.options.find(o => o.route === chosenAnswer);
        if (selectedOption) responseText = selectedOption.feedback;
    }

    if (isCorrect) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      setScore(s => s + 1);
      setFeedback({ type: 'success', text: responseText });
      
      if (newStreak === 3) playFire();
      else playSuccess();
      
      // UX Calibration: 2000ms comprehension exit for success state
      nextCard(2000);
    } else {
      if (card.interventionId) {
          playExtinguish();
          const logic = failureLogic?.[card.interventionId] || {
              title: "Critical Error",
              script: "You missed a vital signal.",
              videoUrl: null
          };
          setActiveIntervention(logic);
          setStage('intervention');
      } else {
          if (streak > 0) {
            setIsExtinguished(true);
            playExtinguish();
          } else {
            playError();
          }
          setStreak(0); 
          setFeedback({ type: 'error', text: responseText || "Missed it!" });
          
          // PATCH: 4000ms delay allowing user to read feedback before auto-advancing
          nextCard(4000);
      }
    }
  };

  const nextCard = (delay = 1000) => {
    setTimeout(() => {
      setFeedback(null);
      setIsExtinguished(false);
      x.set(0); 
      y.set(0);

      if (index + 1 < activeDeck.length) {
        setIndex(i => i + 1);
      } else {
        setStage('results');
        setTimeout(() => playWin(), 200);
      }
    }, delay);
  };

  const handleRetry = () => {
    // TELEMETRY PATCH: Push failure iteration to session history array
    const percentage = Math.round((score / activeDeck.length) * 100);
    setSessionAttempts(prev => [...prev, { status: 'retry', timestamp: Date.now(), score: percentage, answers: userAnswers }]);

    setIndex(0);
    setStreak(0);
    setScore(0);
    setUserAnswers([]);
    
    // Reshuffle on retry to maintain anti-pattern enforcement
    let sourceDeck = [];
    let sliceCount = 10;
    
    if (id === 'game_channel_surfer') {
        sourceDeck = mediumMattersBank;
        sliceCount = 10;
    } else if (id === 'game_gatekeeper') {
        sourceDeck = gatekeepingBank;
        sliceCount = 5;
    } else if (id === 'tech-stack' || id === 'game_tech_stack') {
        sourceDeck = techStackBank;
        sliceCount = 10;
    } else if (id === 'game_culture_vibe' || id === 'game_dress_code' || id === 'game_expense_roulette' || id === 'game_office_etiquette') {
        sourceDeck = gameData?.deck || [];
        sliceCount = 10;
    } else {
        sourceDeck = gameData?.deck || [];
        sliceCount = sourceDeck.length;
    }

    let array = [...sourceDeck];
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    deckRef.current = array.slice(0, sliceCount);
    
    setStage('playing');
    setIsExtinguished(false);
    setActiveIntervention(null);
    x.set(0);
    y.set(0);
    cardMountTimeRef.current = Date.now();
  };

  // PATCH: Chromium network suspension trap
  const handleFinalSubmit = async (percentage, passed) => {
      const interactions = [
        ...sessionAttempts,
        { status: passed ? 'success' : 'failed', timestamp: Date.now(), score: percentage, answers: userAnswers }
      ];

      // Dispatch the payload safely
      if (typeof onComplete === 'function') {
          try {
              setIsSubmitting(true);
              await onComplete({ id, rawInteractions: interactions }); 
          } catch (error) {
              console.error("Submission Failed:", error);
              if (error.message?.includes('network') || error.code === 'unavailable') {
                  alert("Network connection suspended by the browser. Please refresh the page and try again.");
              } else {
                  alert("An error occurred saving your data. Please try again.");
              }
          } finally {
              setIsSubmitting(false);
          }
      }
  };

  // --- RENDER RESULTS ---
  if (stage === 'results') {
    const percentage = Math.round((score / activeDeck.length) * 100);
    const passed = percentage >= 80;

    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-xl p-8 text-center animate-fade-in">
        <div className={`mb-6 p-6 rounded-full ${passed ? 'bg-green-100' : 'bg-red-100'}`}>
          {passed ? <Trophy className="w-16 h-16 text-green-600" /> : <XCircle className="w-16 h-16 text-red-600" />}
        </div>
        
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {passed ? (percentage === 100 ? "FLAWLESS VICTORY!" : "Mission Accomplished") : "Mission Failed"}
        </h2>
        
        <p className="text-gray-600 mb-8 text-lg">
          You scored <span className={`font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>{score}/{activeDeck.length}</span> ({percentage}%).
          {!passed && <span className="block mt-2 text-sm">You need 80% to proceed.</span>}
        </p>

        {passed ? (
          <button
            onClick={() => handleFinalSubmit(percentage, passed)} 
            disabled={isSubmitting}
            className="w-full max-w-xs bg-green-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-green-700 transition transform hover:scale-105 flex items-center justify-center disabled:opacity-50"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
            Continue
          </button>
        ) : (
          <button
            onClick={handleRetry}
            className="w-full max-w-xs bg-gray-800 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-gray-900 transition transform hover:scale-105 flex items-center justify-center"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (!card) return null;

  // --- RENDER PLAYING ---
  return (
    <div className="h-full min-h-[500px] flex flex-col bg-slate-50 rounded-xl overflow-hidden border border-gray-200 relative">
      
      {stage === 'briefing' && preGameBriefing && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay type="briefing" data={preGameBriefing} onContinue={() => {
                setStage('playing');
                cardMountTimeRef.current = Date.now();
            }} />
          </div>
      )}

      {stage === 'intervention' && activeIntervention && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay type="intervention" data={activeIntervention} onRetry={handleRetry} />
          </div>
      )}

      {/* HEADER */}
      {(stage === 'playing' || stage === 'results') && (
        <div className="bg-white p-4 border-b flex justify-between items-center shadow-sm z-10 w-full">
          <div className="flex items-center gap-3">
              <button 
                  onClick={() => setStage('briefing')} 
                  className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
                  title="Review Mission Briefing"
              >
                  <HelpCircle size={16} />
              </button>
              <div className="hidden sm:flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Session</span>
                  <span className="text-sm font-bold text-gray-800">{gameData?.title || 'Swipe Triage'}</span>
              </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block w-24 bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${((index) / activeDeck.length) * 100}%` }}></div>
            </div>
            
            <AnimatePresence mode='wait'>
              {isExtinguished ? (
                <motion.div
                  key="extinguished"
                  initial={{ scale: 1.5, rotate: -10 }}
                  animate={{ scale: 1, rotate: 0, x: [0, -10, 10, -10, 10, 0] }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="flex items-center px-3 py-1 rounded-full font-bold border-2 bg-gray-800 text-white border-gray-600 shadow-lg"
                >
                  <CloudRain className="w-5 h-5 mr-1.5 text-blue-300 animate-bounce" />
                  <span className="text-sm font-bold">SNUFFED!</span>
                </motion.div>
              ) : (
                <motion.div
                  key="normal"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`flex items-center px-3 py-1 rounded-full font-bold border-2 transition-colors duration-300 ${
                    streak >= 3 
                      ? 'bg-orange-100 text-orange-600 border-orange-300 shadow-[0_0_15px_rgba(251,146,60,0.6)]' 
                      : streak > 0 
                        ? 'bg-blue-50 text-blue-600 border-blue-200' 
                        : 'bg-gray-100 text-gray-400 border-gray-200'
                  }`}
                >
                  <Flame className={`w-5 h-5 mr-1.5 ${streak >= 3 ? 'fill-orange-500 animate-pulse' : ''}`} />
                  <span className="text-sm sm:text-base whitespace-nowrap">
                    {streak >= 3 ? "ON FIRE! " : ""}{streak}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* CARD AREA */}
      <div className="relative flex-grow w-full flex flex-col items-center justify-center p-4">
        {config.mode === 'horizontal' && layout.left && (
          <>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-0 opacity-20">
              <layout.left.icon className="w-32 h-32 text-gray-500" />
            </div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-0 opacity-20">
              <layout.right.icon className="w-32 h-32 text-gray-500" />
            </div>
          </>
        )}

        <motion.div
          drag={stage === 'playing' ? (config.mode === 'horizontal' ? 'x' : 'y') : false}
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragElastic={0.7}
          style={{ x, y, rotate, opacity }}
          onDragEnd={handleDragEnd}
          className="w-full max-w-xs aspect-[3/4] bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col items-center justify-center p-6 text-center cursor-grab active:cursor-grabbing z-20 relative"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">{card.type === 'visual' ? '📸 Vibe Check' : 'Scenario:'}</h3>
          
          <p className="text-lg text-gray-700 leading-relaxed">{card.scenarioText || card.artifactDescription || card.text}</p>
          
          <div className="absolute bottom-6 text-xs text-gray-400 font-medium">
            {config.instruction}
          </div>

          <AnimatePresence>
            {feedback && (
              <motion.div 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`absolute inset-0 flex items-center justify-center rounded-2xl bg-white/95 backdrop-blur-md border-4 p-6 ${feedback.type === 'success' ? 'border-green-500' : 'border-red-500'}`}
              >
                <div className="text-center flex flex-col items-center">
                  {feedback.type === 'success' ? <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" /> : <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />}
                  
                  <h3 className={`text-xl font-black uppercase mb-2 ${feedback.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                    {feedback.type === 'success' ? 'Correct' : 'Incorrect'}
                  </h3>
                  <p className={`font-medium text-sm leading-snug ${feedback.type === 'success' ? 'text-green-900' : 'text-red-900'}`}>
                    {feedback.text}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <div className="w-full mt-auto pb-4">
        {config.mode === 'horizontal' && layout.left && (
          <div className="flex justify-between px-4">
            <div className={`flex flex-col items-center ${layout.left.color} p-3 rounded-lg w-32`}>
              <ChevronLeft className="w-6 h-6 mb-1" />
              <span className="text-xs font-bold text-center leading-tight">{layout.left.text}</span>
            </div>
            <div className={`flex flex-col items-center ${layout.right.color} p-3 rounded-lg w-32`}>
              <ChevronRight className="w-6 h-6 mb-1" />
              <span className="text-xs font-bold text-center leading-tight">{layout.right.text}</span>
            </div>
          </div>
        )}
        {config.mode === 'vertical' && (
          <div className="flex flex-col items-center gap-2 text-gray-500 text-sm font-bold">
            <div className="flex items-center gap-1"><ChevronUp /> {config.upLabel.text}</div>
            <div className="h-8 w-px bg-gray-300"></div>
            <div className="flex items-center gap-1"><ChevronDown /> {config.downLabel.text}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwipeGame;